import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { usersApi } from '@/lib/api';
import type { AppNotification } from '@/lib/api';
import { ToastContainer } from '@/components/ui/Toast';
import { OrgSwitcher } from '@/components/OrgSwitcher';

const loginPageImport = () => import('@/pages/LoginPage');
const authCallbackPageImport = () => import('@/pages/AuthCallbackPage');
const dashboardPageImport = () => import('@/pages/DashboardPage');
const projectPageImport = () => import('@/pages/ProjectPage');
const calendarPageImport = () => import('@/pages/CalendarPage');
const teamsPageImport = () => import('@/pages/TeamsPage');
const teamPageImport = () => import('@/pages/TeamPage');
const organizationsPageImport = () => import('@/pages/OrganizationsPage');
const organizationPageImport = () => import('@/pages/OrganizationPage');
const notificationsPageImport = () => import('@/pages/NotificationsPage');
const feedbackQueuePageImport = () => import('@/pages/FeedbackQueuePage');
const invitePageImport = () => import('@/pages/InvitePage');
const magicLinkPageImport = () => import('@/pages/MagicLinkPage');
const settingsPageImport = () => import('@/pages/SettingsPage');
const listsPageImport = () => import('@/pages/ListsPage');

const LoginPage = lazy(loginPageImport);
const AuthCallbackPage = lazy(authCallbackPageImport);
const DashboardPage = lazy(dashboardPageImport);
const ProjectPage = lazy(projectPageImport);
const CalendarPage = lazy(calendarPageImport);
const TeamsPage = lazy(teamsPageImport);
const TeamPage = lazy(teamPageImport);
const OrganizationsPage = lazy(organizationsPageImport);
const OrganizationPage = lazy(organizationPageImport);
const NotificationsPage = lazy(notificationsPageImport);
const FeedbackQueuePage = lazy(feedbackQueuePageImport);
const InvitePage = lazy(invitePageImport);
const MagicLinkPage = lazy(magicLinkPageImport);
const SettingsPage = lazy(settingsPageImport);
const ListsPage = lazy(listsPageImport);

// Once the authenticated shell mounts, prefetch the other page chunks during
// idle time so navigating between them doesn't hit the Suspense fallback.
function prefetchAppChunks() {
  [
    dashboardPageImport,
    projectPageImport,
    calendarPageImport,
    teamsPageImport,
    teamPageImport,
    organizationsPageImport,
    organizationPageImport,
    notificationsPageImport,
    feedbackQueuePageImport,
    settingsPageImport,
    listsPageImport,
  ].forEach((load) => {
    load().catch(() => {
      /* ignore — will retry on actual navigation */
    });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number })?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 3;
      },
      // 1s → 2s → 4s, capped at 30s
      retryDelay: (failureCount) => Math.min(1000 * 2 ** failureCount, 30_000),
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const sessionExpired = useAuthStore((s) => s.sessionExpired);
  if (!isAuthenticated)
    return <Navigate to={sessionExpired ? '/login?expired=1' : '/login'} replace />;
  return <>{children}</>;
}

// ── Notification Bell ────────────────────────────────────────────────────

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => usersApi.getNotifications(),
    refetchInterval: 30_000,
  });

  const unread = notifications.filter((n) => !n.readAt).length;
  const recent = notifications.slice(0, 8);

  const markRead = useMutation({
    mutationFn: (id: string) => usersApi.markNotificationRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['notifications'] });
      const previous = qc.getQueryData<AppNotification[]>(['notifications']);
      qc.setQueryData<AppNotification[]>(
        ['notifications'],
        (prev) =>
          prev?.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)) ?? [],
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) qc.setQueryData(['notifications'], context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: () => usersApi.markAllNotificationsRead(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['notifications'] });
      const previous = qc.getQueryData<AppNotification[]>(['notifications']);
      qc.setQueryData<AppNotification[]>(
        ['notifications'],
        (prev) => prev?.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })) ?? [],
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(['notifications'], context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => usersApi.dismissNotification(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['notifications'] });
      const previous = qc.getQueryData<AppNotification[]>(['notifications']);
      qc.setQueryData<AppNotification[]>(
        ['notifications'],
        (prev) => prev?.filter((n) => n.id !== id) ?? [],
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) qc.setQueryData(['notifications'], context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClick = (n: AppNotification) => {
    if (!n.readAt) markRead.mutate(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  const typeIcon: Record<AppNotification['type'], string> = {
    task_assigned: '📋',
    team_added: '👥',
    project_added: '📁',
    status_changed: '🔄',
    feedback_received: '💬',
    mention_in_comment: '@',
    mention_in_feedback: '@',
    mention_in_work_log: '@',
    mention_in_description: '@',
    task_due_soon: '⏰',
    task_overdue: '🚨',
    project_removed: '🚪',
    feedback_requested: '📝',
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative cursor-pointer rounded-lg border border-(--border) bg-transparent px-2.5 py-1.5 text-base leading-none text-(--text-3)"
      >
        🔔
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-4 rounded-[10px] bg-(--danger) px-[5px] py-px text-center text-[10px] leading-[14px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute bottom-full left-0 z-100 mb-2 w-80 overflow-hidden rounded-xl border border-(--border-subtle) bg-(--bg-card) shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          <div className="flex items-center justify-between border-b border-(--border-subtle) px-4 py-3.5">
            <span className="text-sm font-bold text-(--text)">Notifications</span>
            {unread > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="cursor-pointer border-none bg-transparent text-xs text-(--accent)"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-90 overflow-y-auto">
            {recent.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-(--text-3)">
                No notifications yet
              </div>
            ) : (
              recent.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`flex gap-2.5 border-b border-(--border-subtle) px-4 py-3 ${n.link ? 'cursor-pointer' : 'cursor-default'} ${n.readAt ? 'bg-transparent' : 'bg-(--accent-bg)'}`}
                >
                  <span className="flex-shrink-0 text-lg leading-[1.3]">{typeIcon[n.type]}</span>
                  <div className="min-w-0 flex-1">
                    <div
                      className={`mb-0.5 text-[13px] text-(--text) ${n.readAt ? 'font-normal' : 'font-semibold'}`}
                    >
                      {n.title}
                    </div>
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-(--text-3)">
                      {n.body}
                    </div>
                    <div className="mt-1 text-[11px] text-(--text-4)">
                      {new Date(n.createdAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 flex-col items-center gap-1.5">
                    {!n.readAt && <div className="mt-1 h-2 w-2 rounded-full bg-(--accent)" />}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dismiss.mutate(n.id);
                      }}
                      title="Dismiss"
                      className="cursor-pointer border-none bg-transparent p-0.5 text-base leading-none text-(--text-4)"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <button
            onClick={() => {
              navigate('/notifications');
              setOpen(false);
            }}
            className="block w-full cursor-pointer border-none border-t border-t-(--border-subtle) bg-transparent px-4 py-3 text-center text-[13px] font-semibold text-(--accent)"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}

// ── App Shell ────────────────────────────────────────────────────────────

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuthStore();
  const { setTheme } = useUiStore();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sync theme from server preferences on mount
  useEffect(() => {
    usersApi
      .getPreferences()
      .then((prefs) => {
        setTheme(prefs.theme as 'dark' | 'light');
      })
      .catch(() => {
        /* ignore — use stored theme */
      });
    // Run once on auth shell mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefetch other page chunks during idle time so navigation feels instant
  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(prefetchAppChunks);
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(prefetchAppChunks, 200);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <div className="flex h-screen bg-(--bg) font-sans text-(--text)">
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
          data-testid="sidebar-backdrop"
          className="fixed inset-0 z-190 bg-black/40 md:hidden"
        />
      )}

      {/* Sidebar */}
      <nav
        data-open={drawerOpen}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('a')) setDrawerOpen(false);
        }}
        className="fixed inset-y-0 left-0 z-200 flex w-[220px] -translate-x-full flex-col border-r border-(--border-subtle) bg-(--bg-card) px-4 pt-[calc(1.5rem+env(safe-area-inset-top))] pb-[calc(1.5rem+env(safe-area-inset-bottom))] transition-transform duration-200 data-[open=true]:translate-x-0 md:static md:translate-x-0"
      >
        <div
          onClick={() => navigate('/dashboard')}
          className="mb-8 cursor-pointer text-lg font-extrabold text-(--accent)"
        >
          ProjectFlow
        </div>

        <OrgSwitcher />

        <div className="flex flex-1 flex-col gap-1">
          {[
            { to: '/dashboard', label: '⊞ Projects' },
            { to: '/lists', label: '☑ Lists' },
            { to: '/calendar', label: '📅 Calendar' },
            { to: '/teams', label: '👥 Teams' },
            { to: '/feedback-queue', label: '📝 Feedback Queue' },
          ].map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `rounded-lg px-3 py-2.5 text-sm font-semibold no-underline ${
                  isActive ? 'bg-(--accent-bg) text-(--accent)' : 'text-(--text-3)'
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </div>

        {user && (
          <div className="border-t border-(--border-subtle) pt-4">
            <div className="mb-3 flex gap-2">
              <NavLink
                to="/organizations"
                title="Organizations"
                className={({ isActive }) =>
                  `flex items-center justify-center rounded-lg border border-(--border) px-2.5 py-1.5 text-base leading-none no-underline ${
                    isActive ? 'text-(--accent)' : 'text-(--text-3)'
                  }`
                }
              >
                🏢
              </NavLink>
              <NotificationBell />
            </div>
            <button
              onClick={() => navigate('/settings')}
              className="mb-2 flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-1 py-1.5"
            >
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.name}
                  className="h-7 w-7 flex-shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-(--accent-bg) text-xs font-bold text-(--accent)">
                  {user.name[0]?.toUpperCase()}
                </div>
              )}
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-(--text-2)">
                {user.name}
              </span>
            </button>
            <button
              onClick={() => logout()}
              className="w-full cursor-pointer rounded-md border border-(--border) px-3 py-1.5 text-xs text-(--text-3)"
            >
              Sign out
            </button>
          </div>
        )}
      </nav>

      {/* Content column: mobile header bar sits in normal flow above main,
          so it never overlaps a page's own heading the way a fixed-position
          toggle would. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-150 flex flex-shrink-0 items-center border-b border-(--border-subtle) bg-(--bg-card) px-4 py-2.5 pt-[calc(0.625rem+env(safe-area-inset-top))] md:hidden">
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-(--border) bg-(--bg) text-(--text-2)"
          >
            ☰
          </button>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-8">{children}</main>
      </div>
      <ToastContainer />
    </div>
  );
}

export default function App() {
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const user = useAuthStore((s) => s.user);
  const refreshSession = useAuthStore((s) => s.refreshSession);
  // If there's a stored refresh token but no user in memory (new tab or page refresh),
  // attempt to restore the session before rendering so auth-gated pages work correctly.
  const [hydrated, setHydrated] = React.useState(!refreshToken || !!user);

  React.useEffect(() => {
    if (!hydrated) {
      refreshSession().finally(() => setHydrated(true));
    }
    // refreshSession is a stable Zustand function reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-(--bg) text-(--text-2)">
        Loading…
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<div className="p-8 text-(--text-2)">Loading...</div>}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/auth/magic" element={<MagicLinkPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <Routes>
                      <Route index element={<Navigate to="/dashboard" replace />} />
                      <Route path="dashboard" element={<DashboardPage />} />
                      <Route path="projects/:projectId" element={<ProjectPage />} />
                      <Route path="lists" element={<ListsPage />} />
                      <Route path="lists/:listId" element={<ListsPage />} />
                      <Route path="calendar" element={<CalendarPage />} />
                      <Route path="teams" element={<TeamsPage />} />
                      <Route path="teams/:teamId" element={<TeamPage />} />
                      <Route path="organizations" element={<OrganizationsPage />} />
                      <Route path="organizations/:orgId" element={<OrganizationPage />} />
                      <Route path="notifications" element={<NotificationsPage />} />
                      <Route path="feedback-queue" element={<FeedbackQueuePage />} />
                      <Route path="settings" element={<SettingsPage />} />
                    </Routes>
                  </AppShell>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
